import type { ContentAccess } from "@shared/content";
import { isError } from "@shared/engine";
import {
  addPlayer, applyAction, botPlans, buildView, createRoomCore, nextWake, runTimers, type HostDeps,
} from "@shared/room";
import { randomSeed } from "@shared/rng";
import type { ActResult, ErrorCode, GameId, Lang, RoomAction, RoomCore, RoomView } from "@shared/types";
import { storage } from "./store";
import { Emitter, type RoomClient, type RoomStatus } from "./room-client";

export interface LocalSetup {
  game: GameId;
  lang: Lang;
  settings?: unknown;
  players: { name: string; avatar?: number; isBot?: boolean }[];
}

const PREFIX = "hg:local:";

function localCode(): string {
  const A = "ABCDEFGHJKMNPQRSTUVWXYZ";
  let s = "L";
  for (let i = 0; i < 4; i++) s += A[Math.floor(Math.random() * A.length)];
  return s;
}

/**
 * A room hosted entirely in this browser: the same engines and room rules as the server,
 * with timers and bots scheduled here. Used for pass-and-play, hot-seat and the preview build.
 */
export class LocalRoom implements RoomClient {
  readonly local = true;
  readonly code: string;
  readonly core: RoomCore;
  meId: string;
  private em = new Emitter();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private bots = new Map<string, { key: string; t: ReturnType<typeof setTimeout> }>();
  private cached: RoomView | null = null;
  private closed = false;

  constructor(private content: ContentAccess, init: { setup?: LocalSetup; restore?: RoomCore }) {
    if (init.restore) {
      this.core = init.restore;
      // deadlines are wall-clock times: after a reload the game waits, paused, for the players
      if (this.core.phase === "game" && this.core.state && !this.core.paused) this.core.paused = { at: this.core.updatedAt };
      this.code = this.core.code;
      this.meId = this.core.hostId || this.core.players[0]?.id || "";
    } else {
      const s = init.setup!;
      this.code = localCode();
      this.core = createRoomCore({ code: this.code, game: s.game, lang: s.lang, local: true, seed: randomSeed(), now: Date.now(), settings: s.settings });
      let first = "";
      for (const p of s.players) {
        const rec = addPlayer(this.core, { name: p.name, isBot: p.isBot, avatar: p.avatar }, this.deps());
        if (!isError(rec) && !first && !rec.isBot) first = rec.id;
      }
      this.core.hostId = first || this.core.players[0]?.id || "";
      this.meId = this.core.hostId;
    }
    this.after(false);
  }

  static restore(code: string, content: ContentAccess): LocalRoom | null {
    const core = storage.get<RoomCore | null>(PREFIX + code, null);
    if (!core || !core.local) return null;
    return new LocalRoom(content, { restore: core });
  }

  static saved(): { code: string; game: GameId; updatedAt: number }[] {
    const out: { code: string; game: GameId; updatedAt: number }[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k?.startsWith(PREFIX)) continue;
        const c = storage.get<RoomCore | null>(k, null);
        if (c) out.push({ code: c.code, game: c.game, updatedAt: c.updatedAt });
      }
    } catch {
      /* ignore */
    }
    return out.sort((a, b) => b.updatedAt - a.updatedAt);
  }

  private deps(): HostDeps {
    return { now: Date.now(), content: this.content };
  }

  view(): RoomView | null {
    if (!this.cached) this.cached = buildView(this.core, this.meId, Date.now());
    return this.cached;
  }
  viewAs(playerId: string): RoomView {
    return buildView(this.core, playerId, Date.now());
  }
  status(): RoomStatus {
    return "live";
  }
  goneReason() {
    return null;
  }
  offset() {
    return 0;
  }
  subscribe(fn: () => void) {
    return this.em.subscribe(fn);
  }

  async act(action: RoomAction): Promise<ActResult> {
    return this.actAs(this.meId, action);
  }

  async actAs(playerId: string, action: RoomAction): Promise<ActResult> {
    if (this.closed) return { ok: false, error: "not_found" };
    const res = applyAction(this.core, playerId, action, this.deps());
    if (res.ok) {
      if (action.t === "leave" && playerId === this.meId) this.meId = this.core.hostId;
      this.after(true);
    }
    return res;
  }

  /** Add a seat on this device (pass-and-play lobby). */
  addSeat(name: string, avatar?: number, isBot = false): ErrorCode | null {
    const rec = addPlayer(this.core, { name, avatar, isBot }, this.deps());
    if (isError(rec)) return rec;
    this.after(true);
    return null;
  }

  private after(changed: boolean) {
    if (this.closed) return;
    this.cached = null;
    this.scheduleTimer();
    this.scheduleBots();
    storage.set(PREFIX + this.code, this.core);
    if (changed) this.em.emit();
  }

  private scheduleTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const wake = nextWake(this.core);
    if (wake === null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (runTimers(this.core, this.deps())) this.after(true);
      else this.scheduleTimer();
    }, Math.max(0, wake - Date.now()) + 5);
  }

  private scheduleBots() {
    const plans = botPlans(this.core, this.deps());
    const wanted = new Set(plans.map((p) => p.botId));
    for (const [id, b] of this.bots) {
      if (!wanted.has(id)) {
        clearTimeout(b.t);
        this.bots.delete(id);
      }
    }
    for (const { botId, plan } of plans) {
      const ex = this.bots.get(botId);
      if (ex && ex.key === plan.key) continue;
      if (ex) clearTimeout(ex.t);
      const t = setTimeout(() => {
        this.bots.delete(botId);
        const res = applyAction(this.core, botId, { t: "g", a: plan.action }, this.deps());
        if (res.ok) this.after(true);
        else this.scheduleBots();
      }, plan.delay);
      this.bots.set(botId, { key: plan.key, t });
    }
  }

  /** Forget this room on the device. */
  discard() {
    storage.del(PREFIX + this.code);
    this.close();
  }

  close() {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    for (const b of this.bots.values()) clearTimeout(b.t);
    this.bots.clear();
  }
}
